import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, wsBase } from "../api.js";
import { cacheGet, cacheSet } from "../outbox.js";
import type { Lang } from "../commentary.js";
import { Empty } from "../ui.js";
import { IconBack } from "../Icons.js";
import { Mark } from "../Mark.js";
import type { Snapshot, Innings } from "./scoreTypes.js";
import { Scorecard, OverByOver, Feed, buildFeed, type VoiceClip } from "./Scorecard.js";
import * as voice from "../voice.js";
import * as rtc from "../rtc.js";
import { Subscriber, type Publication, type SfuState } from "../sfu.js";
import { VoiceBar } from "./VoiceBar.js";

/**
 * The spectator view.
 *
 * Read-only, no lease, no keypad. It is the link you send to a WhatsApp group,
 * so it has to work for someone who has never opened the app and is on a phone
 * at the boundary — which means it paints from cache first and never blocks on
 * a socket.
 */

type Tab = "live" | "card" | "overs";

export function Watch() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [tab, setTab] = useState<Tab>("live");
  const [conn, setConn] = useState<"live" | "polling">("polling");
  const [lang, setLang] = useState<Lang>("en");
  const [saidAloud, setSaidAloud] = useState<VoiceClip[]>([]);

  // What the scorer has said aloud. Polled with the match rather than pushed:
  // a voice note arriving a few seconds late is no worse than one arriving
  // late because the speaker paused.
  useEffect(() => {
    if (!matchId) return;
    const load = () => void api<{ clips: VoiceClip[] }>(`/api/v1/matches/${matchId}/voice`)
      .then((r) => { if (r.ok) setSaidAloud(r.data.clips); });
    load();
    const t = setInterval(() => { if (navigator.onLine) load(); }, 10000);
    return () => clearInterval(t);
  }, [matchId]);
  const [voiceOn, setVoiceOn] = useState(false);
  const spokenUpTo = useRef<number>(-1);
  const ws = useRef<WebSocket | null>(null);
  /** Live-audio signalling shares this socket. See the scorer for the pairing. */
  const rtcIn = useRef<((m: Record<string, unknown>) => void) | null>(null);
  const [talking, setTalking] = useState(false);
  const [loadError, setLoadError] = useState<"gone" | "unreachable" | null>(null);
  const sendSignal = useCallback((m: Record<string, unknown>) => {
    const sock = ws.current;
    if (sock && sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify(m));
  }, []);

  useEffect(() => {
    if (!matchId) return;
    void cacheGet<Snapshot>(`snap:${matchId}`).then((h) => { if (h) setSnap(h.value); });
    const load = () => void api<Snapshot>(`/api/v1/matches/${matchId}/snapshot`).then((r) => {
      if (r.ok) { setSnap(r.data); void cacheSet(`snap:${matchId}`, r.data); setLoadError(null); }
      // A 404 is a different problem from a flat network, and a watcher who
      // followed a stale link deserves to be told which. Only surfaced when
      // there is nothing cached to show — an established match should keep
      // rendering through a blip rather than blanking.
      else setLoadError(r.status === 404 ? "gone" : "unreachable");
    });
    load();

    let alive = true, retry = 2000;
    let poll: ReturnType<typeof setInterval> | null = setInterval(() => {
      if (navigator.onLine) load();
    }, 2000);
    const open = () => {
      if (!alive) return;
      let sock: WebSocket;
      try { sock = new WebSocket(`${wsBase()}/api/v1/matches/${matchId}/live?role=spectator`); }
      catch { return; }
      ws.current = sock;
      sock.onopen = () => { retry = 2000; setConn("live"); if (poll) { clearInterval(poll); poll = null; } };
      sock.onclose = () => {
        if (!alive) return;
        setConn("polling");
        if (!poll) poll = setInterval(() => { if (navigator.onLine) load(); }, 2000);
        retry = Math.min(retry * 1.8, 30000);
        setTimeout(open, retry);
      };
      sock.onmessage = (ev) => {
        try {
          const m = JSON.parse(String(ev.data)) as { type?: string; snapshot?: Snapshot; on?: boolean };
          if (m.type === "TALK") { setTalking(m.on === true); return; }
          if (typeof m.type === "string" && m.type.startsWith("RTC_")) {
            rtcIn.current?.(m as unknown as Record<string, unknown>);
            return;
          }
          if (m.snapshot) { setSnap(m.snapshot); void cacheSet(`snap:${matchId}`, m.snapshot); }
        } catch { /* ignore malformed frames */ }
      };
    };
    open();
    return () => { alive = false; if (poll) clearInterval(poll); try { ws.current?.close(); } catch { /* */ } };
  }, [matchId]);

  useEffect(() => {
    if (!voiceOn || !snap) return;
    const lines = buildFeed(snap, (id) => snap.meta.players[id] ?? "—", lang);
    if (spokenUpTo.current < 0) { spokenUpTo.current = lines.length; return; }
    const fresh = lines.slice(spokenUpTo.current);
    spokenUpTo.current = lines.length;
    for (const l of fresh) voice.say(l.text, l.kind, lang, { interrupt: l.kind === "result", bank: l.bank ?? null });
  }, [snap, voiceOn, lang]);

  if (!snap) {
    /**
     * This said "Loading the match…" and nothing else, for ever.
     *
     * A watcher following a link to a match that had been deleted, or opening
     * one with no signal, waited on a message that would never change. It is
     * the most-shared screen in the app and the one most likely to be opened
     * by somebody who cannot debug it.
     */
    if (loadError === "gone") {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
          <p className="text-lead font-semibold">That match is not here</p>
          <p className="text-body leading-relaxed text-faint">
            The link may be out of date, or the match may have been removed.
          </p>
        </div>
      );
    }
    if (loadError === "unreachable") {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
          <p className="text-lead font-semibold">Cannot reach the match</p>
          <p className="text-body leading-relaxed text-faint">
            {navigator.onLine
              ? "The server did not answer. This screen will pick up on its own once it does."
              : "You are offline. This will load as soon as you have a connection."}
          </p>
        </div>
      );
    }
    return <div className="flex h-full items-center justify-center text-body text-faint">Loading the match…</div>;
  }

  const st = snap.state;
  const inn: Innings | undefined = st.innings[st.currentInnings];
  const rs = snap.meta.ruleset;
  const per = rs.ballsPerOver;
  const name = (id: string) => snap.meta.players[id] ?? "—";
  const teamOf = (id: string) => snap.meta.teams[id]?.short ?? "";

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-2.5 border-b border-line px-4 pb-2.5
                         pt-[calc(10px+var(--sat))]">
        <button onClick={() => navigate(-1)} aria-label="Back"
          className="flex h-11 w-11 shrink-0 items-center justify-center text-faint"><IconBack /></button>
        <span className="shrink-0"><Mark size={28} /></span>
        {/* The title is what gives way when the header runs out of room —
            not the back button, which measured 22px wide on a 320px screen. */}
        <span className="hidden font-num text-body font-semibold tracking-[.18em]
                         min-[360px]:inline">WATCHING</span>
        <div className="ml-auto flex items-center gap-2">
          <ListenButton sendSignal={sendSignal} rtcIn={rtcIn} talking={talking}
                        connected={conn === "live"} />
          <span className="flex items-center gap-1.5 font-mono text-meta text-faint">
            <i className={`h-[7px] w-[7px] rounded-full ${conn === "live" ? "bg-crease" : "bg-amber"}`} />
            {conn === "live" ? "live" : "updating"}
          </span>
        </div>
      </header>

      <div className="flex shrink-0 border-b border-line">
        {([["live","Live"],["card","Scorecard"],["overs","Overs"]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex-1 py-3 font-mono text-meta uppercase tracking-[.14em]
              ${tab === k ? "border-b-2 border-amber text-amber" : "text-faint"}`}>
            {label}
          </button>
        ))}
      </div>

      <main className="min-h-0 flex-1 overflow-y-auto pb-6">
        <div className="mx-auto w-full max-w-[760px]">
          {!inn ? (
            <Empty title="Not started">This match has not begun yet.</Empty>
          ) : tab === "live" ? (
            <LiveTab snap={snap} inn={inn} per={per} name={name} teamOf={teamOf}
                     lang={lang} setLang={setLang} voiceOn={voiceOn} saidAloud={saidAloud}
                     setVoiceOn={(v) => { spokenUpTo.current = -1; setVoiceOn(v); }} />
          ) : tab === "card" ? (
            <Scorecard snap={snap} name={name} />
          ) : (
            <OverByOver snap={snap} name={name} />
          )}
        </div>
      </main>
    </div>
  );
}

function LiveTab({ snap, inn, per, name, teamOf, lang, setLang, voiceOn, setVoiceOn, saidAloud }: {
  snap: Snapshot; inn: Innings; per: number;
  name: (id: string) => string; teamOf: (id: string) => string;
  lang: Lang; setLang: (l: Lang) => void;
  voiceOn: boolean; setVoiceOn: (v: boolean) => void;
  saidAloud: VoiceClip[];
}) {
  const st = snap.state;
  const rs = snap.meta.ruleset;
  const strikerEnd = inn.bowlerEnd === "END_A" ? "END_B" : "END_A";
  const rr = inn.legalBalls ? inn.totalRuns / (inn.legalBalls / per) : 0;
  const target = st.target;
  const need = target !== null ? target - inn.totalRuns : null;
  const left = rs.oversPerInnings * per - inn.legalBalls;

  return (
    <div className="px-4 pt-4 split:px-6">
      <div className="font-mono text-meta uppercase tracking-[.13em] text-faint">
        {snap.meta.teams[inn.battingTeamId]?.name} batting
      </div>
      <div className="flex items-baseline gap-3.5 pt-1">
        <div className="font-num text-mega font-semibold leading-none tracking-tight">
          {inn.totalRuns}<span className="text-[#8B95A1]">/{inn.wickets}</span>
        </div>
        <div className="ml-auto text-right">
          <b className="block font-num text-big font-semibold leading-none">
            {Math.floor(inn.legalBalls / per)}.{inn.legalBalls % per}
          </b>
          <span className="font-mono text-label tracking-[.13em] text-faint">OF {rs.oversPerInnings} OV</span>
        </div>
      </div>
      <div className="pt-1.5 font-mono text-caption text-faint">
        RR {rr.toFixed(2)}
        {inn.penaltyRuns !== 0 && <span className="text-leather"> · {inn.penaltyRuns} from wickets</span>}
        {inn.bonusRuns > 0 && <span className="text-crease"> · {inn.bonusRuns} bonus</span>}
      </div>

      {st.complete && st.result && (
        <div className="mt-3 rounded-xl border border-crease bg-[#14251F] px-4 py-3 text-body font-semibold text-crease">
          {teamOf(st.result.winnerTeamId ?? "")} {st.result.summary}
        </div>
      )}
      {!st.complete && need !== null && need > 0 && left > 0 && (
        <div className="mt-3 font-mono text-lead font-semibold text-amber">
          Need {need} from {left} ball{left === 1 ? "" : "s"} · {(need / (left / per)).toFixed(2)}/ov
        </div>
      )}

      {/* at the crease */}
      <h3 className="mt-5 font-mono text-label uppercase tracking-[.14em] text-faint">At the crease</h3>
      <div className="mt-1.5 overflow-hidden rounded-xl border border-line bg-panel">
        {(["END_A", "END_B"] as const).map((end) => {
          const pid = inn.atEnd[end];
          if (!pid) return null;
          const b = inn.batters[pid];
          const sr = b?.ballsFaced ? (b.runs / b.ballsFaced) * 100 : 0;
          return (
            <div key={end} className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
              <span className="w-2.5 text-label text-amber">{end === strikerEnd ? "▸" : ""}</span>
              <span className="min-w-0 flex-1 truncate font-semibold">{name(pid)}</span>
              <span className="font-mono text-lead font-semibold">
                {b?.runs ?? 0} <span className="font-normal text-faint">({b?.ballsFaced ?? 0})</span>
              </span>
              <span className="hidden w-[86px] text-right font-mono text-meta text-faint split:block">
                4×{b?.fours ?? 0} 6×{b?.sixes ?? 0} · {sr.toFixed(0)}
              </span>
            </div>
          );
        })}
      </div>

      {inn.bowlerId && (
        <>
          <h3 className="mt-4 font-mono text-label uppercase tracking-[.14em] text-faint">Bowling</h3>
          <div className="mt-1.5 overflow-hidden rounded-xl border border-line bg-panel">
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="min-w-0 flex-1 truncate font-semibold">{name(inn.bowlerId)}</span>
              <span className="font-mono text-lead font-semibold">
                {Math.floor((inn.bowlers[inn.bowlerId]?.legalBalls ?? 0) / per)}.
                {(inn.bowlers[inn.bowlerId]?.legalBalls ?? 0) % per}–
                {inn.bowlers[inn.bowlerId]?.maidens ?? 0}–
                {inn.bowlers[inn.bowlerId]?.runsConceded ?? 0}–
                {inn.bowlers[inn.bowlerId]?.wickets ?? 0}
              </span>
            </div>
          </div>
        </>
      )}

      {/* this over */}
      <h3 className="mt-4 font-mono text-label uppercase tracking-[.14em] text-faint">This over</h3>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {(snap.deliveries ?? []).slice(-inn.legalBallsThisOver || -6).map((ev, i) => {
          const d = ev.delivery, dr = ev.derived;
          const label = d.wicket ? "W"
            : d.batBoundary === "SIX" ? "6" : d.batBoundary === "FOUR" ? "4"
            : dr.totalRuns === 0 ? "·" : String(dr.totalRuns);
          const tone = d.wicket ? "border-[#3A2422] text-leather"
            : d.batBoundary ? "border-[#4A3A1C] text-amber"
            : (d.byes || d.legByes || d.wide || d.noBall) ? "border-line text-crease"
            : "border-line text-chalk";
          return (
            <span key={i} className={`flex h-10 w-10 items-center justify-center rounded-lg border
                                      bg-panel font-mono text-lead font-semibold ${tone}`}>
              {label}
            </span>
          );
        })}
      </div>

      <div className="mt-5">
        <VoiceBar lang={lang} setLang={setLang} voiceOn={voiceOn} setVoiceOn={setVoiceOn} />
        <Feed snap={snap} name={name} lang={lang} limit={18} voice={saidAloud} />
      </div>
    </div>
  );
}

/**
 * Hear the scorer live.
 *
 * Off by default, and that is not laziness. Turning it on tells the room to ask
 * the scorer's phone for a stream, and the scorer's phone pays for every one of
 * those — so it is opt-in per watcher rather than something everyone silently
 * costs them. It is also the tap that browsers require before any audio is
 * allowed to play at all.
 *
 * When it cannot connect it says so and stops trying to look live. The clips
 * still arrive in the feed either way, which is worth saying out loud: the
 * failure here is a few seconds of latency, not silence.
 */
function ListenButton({ sendSignal, rtcIn, talking, connected }: {
  sendSignal: (m: Record<string, unknown>) => void;
  rtcIn: React.MutableRefObject<((m: Record<string, unknown>) => void) | null>;
  talking: boolean;
  connected: boolean;
}) {
  const [state, setState] = useState<rtc.ListenState>("off");
  const stateRef = useRef<rtc.ListenState>("off");
  const [note, setNote] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const listener = useRef<rtc.Listener | null>(null);
  /**
   * Where the scorer is publishing, if they are.
   *
   * The room announces this once and remembers it, so arriving mid-over still
   * finds the stream. Held in a ref as well as state because the tap handler
   * needs the current value, not the one from the render it was created in.
   */
  const pub = useRef<Publication | null>(null);
  const [hasStream, setHasStream] = useState(false);
  const sub = useRef<Subscriber | null>(null);

  useEffect(() => {
    if (!rtc.supported() || !audio.current) return;
    const l = new rtc.Listener(sendSignal, audio.current, (s, d) => {
      setState(s);
      setNote(d ?? null);
    });
    listener.current = l;
    const s = new Subscriber(audio.current, (st: SfuState, d) => {
      setState(st === "connecting" ? "waiting" : st === "live" ? "live" : st === "failed" ? "failed" : "off");
      setNote(d ?? null);
    });
    sub.current = s;

    rtcIn.current = (m) => {
      if (m["type"] === "RTC_PUBLISH") {
        pub.current = { sessionId: String(m["sessionId"]), trackName: String(m["trackName"]) };
        setHasStream(true);
        // Already listening when the scorer (re)published: follow it across.
        if (stateRef.current !== "off") void s.listen(pub.current);
        return;
      }
      if (m["type"] === "RTC_UNPUBLISH") {
        pub.current = null; setHasStream(false); s.close();
        return;
      }
      void l.handle(m);
    };
    return () => {
      rtcIn.current = null; listener.current = null;
      sub.current = null; s.close(); l.close();
    };
  }, [sendSignal, rtcIn]);

  if (!rtc.supported()) return null;

  const toggle = () => {
    // The SFU when there is a stream to pull, the mesh otherwise. Both are
    // started by this tap, which is also what unlocks audio playback.
    if (state !== "off") {
      sub.current?.close();
      listener.current?.stop();
      return;
    }
    if (pub.current && sub.current) { void sub.current.listen(pub.current); return; }
    listener.current?.start();
  };

  const on = state !== "off";
  const label = state === "live" ? (talking ? "On air" : "Listening")
              : state === "waiting" ? "Waiting"
              : state === "failed" ? "No audio"
              // A stream that is already up is worth advertising: "Listen" on
              // its own gives no hint whether anything is there to hear.
              : hasStream ? "Listen live"
              : "Listen";

  return (
    <>
      {/* Not autoplay: the stream is attached only after the tap above. */}
      <audio ref={audio} autoPlay playsInline className="hidden" />
      <button onClick={toggle} disabled={!connected}
        title={note ?? undefined}
        aria-label={on ? "Stop listening to the commentator" : "Listen to the commentator"}
        className={`flex h-11 items-center gap-1.5 rounded-full border px-3 disabled:opacity-40
          ${state === "live" && talking ? "border-leather bg-leather text-chalk"
            : state === "live" ? "border-crease text-crease"
            : state === "failed" ? "border-line text-faint"
            : on ? "border-amber text-amber" : "border-line text-faint"}`}>
        <i className={`h-[7px] w-[7px] rounded-full
          ${state === "live" && talking ? "bg-chalk animate-pulse" : "bg-current"}`} />
        <span className="font-mono text-label uppercase tracking-[.1em]">{label}</span>
      </button>
    </>
  );
}
